import { select, call, takeLatest, put } from 'redux-saga/effects';

import { ExpenseActions } from '../constants';
import { ExpenseApi } from 'src/services/end-points';
import { StoreState } from '../reducers';

import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteExpenseAction,
    GetExpenseAction,
    GetExpensesAction,
    PersistExpenseAction,
} from '../actions/expense-actions';
import sortingService from '../../services/sorting';

import {
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
} from '../../services/api-handlers/api-resolver';

import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { groupExpense } from '../../helpers/expenses/expense';
import { GetSeancesAction } from '../actions/seance-actions';
import { groupDataToCsv } from '../../helpers/expenses/groupExpenseCsv';
import { exportToCsv } from '../../services/exports/csv';
import { toast } from 'react-toastify';
import _ from 'lodash';

function* handleFetchExpenses(action: GetExpensesAction) {
    const oldData = yield select((state: StoreState) => state.expenses.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;
    let filterProps: any = { ...filters };

    if (filterProps?.expenseDate) {
        filterProps = {
            ...filters,
            ...filterProps?.expenseDate,
        };
        delete filterProps.expenseDate;
    }
    let sortObj = { ...sorting };
    if (_.isEmpty(sortObj)) {
        sortObj = { expenseDate: 'desc' };
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        ExpenseApi.getExpenses({
            ...filterProps,
            ...sortingService.makeOrderSymfony(sortObj),
            page,
            itemsPerPage: 100,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}
function* handleFetchExpensesCsv(action: GetExpensesAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    yield resolveApiCall(
        action,
        () =>
            ExpenseApi.getExpenses({
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage: 5000,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 5000) {
                const actionFiles: GetExpensesAction = {
                    type: ExpenseActions.FETCH_EXPENSES_CSV,
                    payload: {
                        filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 5000,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchExpensesCsv, actionFiles);
            } else {
                const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];
                if (action.all) {
                    const groupCsv = groupDataToCsv(dataAll);
                    exportToCsv('expenses.csv', groupCsv);
                } else {
                    const groupCsvData = groupDataToCsv(data.payload);
                    exportToCsv('expenses.csv', groupCsvData);
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchExpense(action: GetExpenseAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => ExpenseApi.getExpense(id));
}

function* handleDeleteExpense(action: DeleteExpenseAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => ExpenseApi.deleteExpense(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistExpense(action: PersistExpenseAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;
    const dataExpense = groupExpense(restData);
    yield resolveApiCall(
        action,
        () => (id ? ExpenseApi.updateExpense(id, dataExpense) : ExpenseApi.createExpense(dataExpense)),
        function* (data: ResolverApiSuccess) {
            toast.success(`Expense was successfully ${!id ? 'created' : 'updated'}.`);
            yield call(callbacks.resolve);
            if (restData.month) {
                const actionFiles: GetSeancesAction = {
                    type: ExpenseActions.FETCH_EXPENSES,
                    payload: {
                        filters: {
                            'provider.id': restData?.provider,
                            'createdAt[before]': restData.month.monthEnd,
                            'createdAt[after]': restData.month.monthStart,
                        },

                        itemsPerPage: 1200,
                        page: 1,
                    },
                };
                yield call(handleFetchExpenses, actionFiles);
            } else {
                // if (!id) {  }
                yield put(push(Router.generate(routes.EXPENSES_LIST)));
            }
        },

        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (result.error && result.error.response && result.error.response.status) {
                    toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(ExpenseActions.FETCH_EXPENSES, handleFetchExpenses);
    yield takeLatest(ExpenseActions.FETCH_EXPENSES_CSV, handleFetchExpensesCsv);
    yield takeLatest(ExpenseActions.FETCH_EXPENSE, handleFetchExpense);
    yield takeLatest(ExpenseActions.DELETE_EXPENSE, handleDeleteExpense);
    yield takeLatest(ExpenseActions.PERSIST_EXPENSE, handlePersistExpense);
}
